import {Component, inject, Inject, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {SessionService} from './services/session.service';
import { NavigationEnd, RouteConfigLoadEnd, RouteConfigLoadStart, Router, RouterOutlet } from '@angular/router';
import {Subscription} from 'rxjs';
import {DOCUMENT} from '@angular/common';
import {ViewService} from './modules/shared/service/view.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import {FrontendPreset} from './themes/frontend.preset';
import {BackendPreset} from './themes/backend.preset';
import {usePreset } from '@primeng/themes';
import {PrimeNG} from 'primeng/config';
import {DateHelper} from './helper/date.helper';
import {FlutterService} from './services/app/flutter.service';
import {SUPPORTED_LANGUAGES} from '../assets/domains/domains';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [RouterOutlet, ProgressSpinnerModule, ConfirmDialogModule]
})
export class AppComponent implements OnInit, OnDestroy
{
  public flutterService = inject(FlutterService);
  public viewService = inject(ViewService);
  private translate = inject(TranslateService);
  private sessionService = inject(SessionService);
  private router = inject(Router);
  private primeNG = inject(PrimeNG);

  title = 'twok-dart-software';
  private currentPreset: 'frontend' | 'backend' | null = null;

  private readonly supportedLanguages = SUPPORTED_LANGUAGES; //  ['de', 'en', 'nl', 'fr', 'hu'];
  private routeSubscription: Subscription;
  private languageSubscription: Subscription;
  loadingRouteConfig: boolean;

  constructor(@Inject(DOCUMENT) private document: Document)
  {
    this.flutterService.initBridges();

    let languageCode = this.sessionService.languageCode;

    if (!languageCode || !this.supportedLanguages.includes(languageCode))
    {
      // const browserLang = this.translate.getCurrentLang() || this.translate.getBrowserLang();
      const browserLang = this.translate.getBrowserLang();
      console.log('[App] Browser language:', browserLang);
      console.log('[App] Current language:', this.translate.getCurrentLang());

      if (this.supportedLanguages.includes(browserLang))
        languageCode = browserLang;
      else languageCode = 'en'; // Fallback

      console.log('[App] Set language to:', languageCode);
      this.sessionService.languageCode = languageCode;
    }

    this.applyTranslation(languageCode);

    // this.primeNGConfig.setTranslation(new DateHelper().getCalendarLocale(languageCode));

    // document.querySelector('html')?.setAttribute('lang', languageCode);
    // console.log('translate.use: ' + languageCode);
    // translate.get('primeng').subscribe(res => this.config.setTranslation(res));
  }

  ngOnInit(): void
  {
    this.viewService.onResize();

    // Initiale Route prüfen
    this.applyPreset(this.router.url);

    this.routeSubscription = this.router.events.subscribe((event) => {
      if (event instanceof RouteConfigLoadStart)
        this.loadingRouteConfig = true;
      else if (event instanceof RouteConfigLoadEnd)
        this.loadingRouteConfig = false;

      if (event instanceof NavigationEnd)
        this.applyPreset(event.url);
    });
  }

  ngOnDestroy(): void
  {
    if (this.routeSubscription)
    {
      this.routeSubscription.unsubscribe();
      this.routeSubscription = undefined;
    }
    if (this.languageSubscription)
    {
      this.languageSubscription.unsubscribe();
      this.languageSubscription = undefined;
    }
  }

  private applyPreset(url: string): void
  {
    const isFrontend = url.startsWith('/frontend') || url === '/';
    this.viewService.isFrontendSignal$.set(isFrontend);

    if (isFrontend && this.currentPreset !== 'frontend')
    {
      this.currentPreset = 'frontend';
      usePreset(FrontendPreset);
      console.log('usePreset(FRONTEND)');
    }
    else if (url.startsWith('/backend') && this.currentPreset !== 'backend')
    {
      this.currentPreset = 'backend';
      usePreset(BackendPreset);
      console.log('usePreset(BACKEND)');
    }
  }

  private applyTranslation(languageCode: string): void
  {
    // this.translate.setDefaultLang(languageCode); // Immer setzen
    this.translate.use(languageCode);
    console.log('translate.use(' + languageCode + ')');

    this.document.documentElement.lang = languageCode;

    this.primeNG.setTranslation(new DateHelper().getCalendarLocale(languageCode));
  }

}
